import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconTrendingUp, IconTrendingDown, IconMinus } from '@tabler/icons-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@core-ui/Components/ui/tooltip';
import type { KpiValue } from '@types';

interface KpiCardProps {
  label: string;
  icon: React.ReactNode;
  kpiValue: KpiValue;
  formatType: 'number' | 'percent' | 'duration' | 'currency';
  prevPeriod?: { from: Date; to: Date } | null;
  // Only the Active Sessions KPI is a real-time counter; other KPIs may
  // legitimately have `delta=null, trend=neutral, value>0` when there's no
  // previous-period data, and they must not surface a "live now" indicator.
  isLive?: boolean;
  /**
   * Makes the tile a button that drills into the rows behind the number.
   *
   * Only some KPIs have somewhere to go. Given one, the whole card becomes the
   * hit target rather than a small link, and it is a real <button> so it is
   * reachable by keyboard.
   */
  onClick?: () => void;
}

function formatShortDate (d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatPrevPeriodRange (period: { from: Date; to: Date }): string {
  return `${formatShortDate(period.from)} – ${formatShortDate(period.to)}`;
}

function formatValue (value: number, formatType: KpiCardProps['formatType']): string {
  switch (formatType) {
  case 'percent':
    return `${value}%`;
  case 'duration': {
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  }
  case 'currency':
    if (value >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(1)}M`;
    }
    if (value >= 1_000) {
      return `$${(value / 1_000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  default:
    return value.toLocaleString();
  }
}

function formatDelta (delta: number | null, deltaType: KpiValue['deltaType']): string {
  if (delta === null || deltaType === null) {
    return '';
  }

  const prefix = delta > 0 ? '+' : '';

  switch (deltaType) {
  case 'percentage':
    return `${prefix}${delta}%`;
  case 'points':
    return `${prefix}${delta}pts`;
  case 'seconds':
    return `${prefix}${delta}s`;
  case 'absolute':
    return `${prefix}${delta}`;
  default:
    return `${prefix}${delta}`;
  }
}

export default function KpiCard ({ label, icon, kpiValue, formatType, prevPeriod, isLive = false, onClick }: KpiCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const Root = onClick !== undefined ? 'button' : 'div';
  const trendColor = kpiValue.trend === 'up'
    ? 'text-emerald-500'
    : kpiValue.trend === 'down'
      ? 'text-red-500'
      : 'text-muted-foreground';

  const TrendIcon = kpiValue.trend === 'up'
    ? IconTrendingUp
    : kpiValue.trend === 'down'
      ? IconTrendingDown
      : IconMinus;

  const deltaText = formatDelta(kpiValue.delta, kpiValue.deltaType);

  return (
    <Root
      {...(onClick !== undefined
        ? {
          type: 'button' as const,
          onClick,
          className:
              'w-full text-left rounded-xl border border-border bg-card p-4 transition-colors hover:bg-muted/40 hover:border-border/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer',
        }
        : { className: 'rounded-xl border border-border bg-card p-4' })}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className="text-muted-foreground/40">
          {icon}
        </span>
      </div>
      <div className="text-2xl font-bold tracking-tight text-foreground">
        {formatValue(kpiValue.value, formatType)}
      </div>
      {deltaText !== '' && (
        <div className={`flex items-center gap-1 mt-1 text-xs ${trendColor}`}>
          <TrendIcon size={14} />
          <span>{deltaText}</span>
          {prevPeriod != null ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground ml-0.5 underline decoration-dotted underline-offset-2 cursor-help">
                  {t('dashboard.overview.kpi.vsPrev')}
                </span>
              </TooltipTrigger>
              <TooltipContent>{formatPrevPeriodRange(prevPeriod)}</TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-muted-foreground ml-0.5">{t('dashboard.overview.kpi.vsPrev')}</span>
          )}
        </div>
      )}
      {isLive && kpiValue.value > 0 && (
        <div className="flex items-center gap-1 mt-1 text-xs text-emerald-500">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span className="ml-0.5">{t('dashboard.overview.kpi.liveNow')}</span>
        </div>
      )}
    </Root>
  );
}
